import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Download, Share2, QrCode, Mail, Phone, Globe, Linkedin, Sparkles, Send, Copy, MessageCircle, ExternalLink } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function BusinessCard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ phone: "", title: "", website: "", linkedin_url: "" });
  const [saving, setSaving] = useState(false);
  const [vcard, setVcard] = useState("");
  const cardRef = useRef(null);

  const loadVcard = async () => {
    try {
      const token = localStorage.getItem("prosperr_token");
      const r = await fetch(`${API_BASE}/users/me/vcard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const text = await r.text();
      setVcard(text);
    } catch (_) { /* swallow: non-fatal */ }
  };

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    if (user) setForm({
      phone: user.phone || "",
      title: user.title || "",
      website: user.website || "",
      linkedin_url: user.linkedin_url || "",
    });
    loadVcard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const publicUrl = user?.user_id ? `${window.location.origin}/c/${user.user_id}` : "";

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", form);
      await refresh();
      await loadVcard();
      toast.success("Informations enregistrées");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const downloadVcf = () => {
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(user?.name || "contact").replace(/\s/g, "-")}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Carte téléchargée");
  };

  const share = async () => {
    const text = `${user?.name || ""}\n${form.title || ""}\n${profile?.company_name || ""}\n${user?.email || ""}${form.phone ? "\n" + form.phone : ""}${publicUrl ? "\n" + publicUrl : ""}`;
    if (navigator.share) {
      try { await navigator.share({ title: user?.name, text, url: publicUrl }); } catch (_) { /* swallow: non-fatal */ }
    } else {
      navigator.clipboard.writeText(publicUrl || text);
      toast.success("Lien copié");
    }
  };

  const inputCls = "h-12 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-emerald-500 focus-visible:ring-0 px-0 text-base bg-transparent text-[var(--text)]";

  return (
    <AppShell>
      <div className="px-5 pt-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-full -ml-2 text-zinc-400 hover:text-white hover:bg-[var(--surface-alt)]" data-testid="back">
          <ArrowLeft className="w-4 h-4 mr-1" />Retour
        </Button>
      </div>
      <div className="px-5 mt-2">
        <div className="hp-label">Profil</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[var(--text)] flex items-center gap-2 tracking-tight" data-testid="card-title">
          <QrCode className="w-5 h-5 text-emerald-400" strokeWidth={1.8} />Ma carte de visite
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Partagez vos coordonnées en un scan.</p>
      </div>

      {/* Card preview — premium dark with violet accent */}
      <div className="px-5 mt-5">
        <div ref={cardRef} className="rounded-2xl overflow-hidden border border-[var(--border)] bg-[var(--surface)] relative">
          <div className="brand-glow absolute inset-0 pointer-events-none opacity-70" />
          <div className="p-6 relative">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-emerald-500/15 border border-emerald-500/30 backdrop-blur-sm overflow-hidden grid place-items-center shrink-0">
                {user?.picture ? (
                  <img src={user.picture} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display text-2xl font-semibold text-emerald-300">{(user?.name || "?").charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display text-xl font-semibold text-[var(--text)] truncate" data-testid="card-name">{user?.name}</div>
                {form.title && <div className="text-xs text-zinc-400 truncate">{form.title}</div>}
                <div className="text-xs text-zinc-500 truncate">{profile?.company_name || "—"}</div>
              </div>
            </div>

            <div className="mt-6 space-y-2.5 text-sm">
              {user?.email && <div className="flex items-center gap-2.5 text-zinc-300"><Mail className="w-4 h-4 text-emerald-400 shrink-0" />{user.email}</div>}
              {form.phone && <div className="flex items-center gap-2.5 text-zinc-300"><Phone className="w-4 h-4 text-emerald-400 shrink-0" />{form.phone}</div>}
              {form.website && <div className="flex items-center gap-2.5 text-zinc-300 truncate"><Globe className="w-4 h-4 text-emerald-400 shrink-0" /><span className="truncate">{form.website}</span></div>}
              {form.linkedin_url && <div className="flex items-center gap-2.5 text-zinc-300 truncate"><Linkedin className="w-4 h-4 text-emerald-400 shrink-0" /><span className="truncate">{form.linkedin_url}</span></div>}
            </div>
          </div>

          {/* QR */}
          <div className="px-6 py-5 bg-[var(--surface-alt)] border-t border-[var(--border)] flex items-center justify-between gap-4">
            <div className="text-xs text-zinc-400 flex-1 min-w-0">
              <div className="font-display font-semibold text-[var(--text)] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.8} />Scannable</div>
              <div className="mt-1 leading-relaxed">Le lead scanne avec son appareil photo, voit votre carte et l'ajoute à ses contacts en 1 clic.</div>
              {publicUrl && (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-emerald-400 font-semibold text-[11px] hover:text-emerald-300"
                  data-testid="test-qr-link"
                >
                  <ExternalLink className="w-3 h-3" /> Tester le lien
                </a>
              )}
            </div>
            <div className="bg-white rounded-lg p-2 shrink-0" data-testid="vcard-qr">
              {publicUrl && <QRCodeSVG value={publicUrl} size={104} level="M" includeMargin={false} />}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 mt-4 grid grid-cols-2 gap-2">
        <button onClick={share} className="hp-btn hp-btn-primary h-11" data-testid="share-card">
          <Share2 className="w-4 h-4" />Partager
        </button>
        <button onClick={downloadVcf} className="hp-btn hp-btn-secondary h-11" data-testid="download-vcf">
          <Download className="w-4 h-4" />Télécharger .vcf
        </button>
      </div>

      {/* Public link + share */}
      {user?.user_id && (
        <div className="px-5 mt-5">
          <div className="hp-card p-5">
            <div className="font-display font-semibold text-[var(--text)] flex items-center gap-2"><Send className="w-4 h-4 text-emerald-400" strokeWidth={1.8} />Envoyer ma carte à un lead</div>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">Après une visite, envoyez ce lien : le lead voit votre carte, l'ajoute à son téléphone en un clic.</p>
            <PublicLinkShare userId={user.user_id} userName={user.name} company={profile?.company_name} />
          </div>
        </div>
      )}

      {/* Edit form */}
      <div className="px-5 mt-6 pb-8">
        <div className="hp-label mb-2">Modifier mes coordonnées</div>
        <div className="hp-card p-5 space-y-5">
          <div>
            <Label className="hp-label">Fonction</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Directeur Commercial, Gérant..." className={inputCls} data-testid="card-input-title" />
          </div>
          <div>
            <Label className="hp-label">Téléphone</Label>
            <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="card-input-phone" />
          </div>
          <div>
            <Label className="hp-label">Site web</Label>
            <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className={inputCls} data-testid="card-input-website" />
          </div>
          <div>
            <Label className="hp-label">LinkedIn</Label>
            <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="linkedin.com/in/..." className={inputCls} data-testid="card-input-linkedin" />
          </div>
          <button onClick={save} disabled={saving} className="hp-btn hp-btn-primary w-full h-11" data-testid="save-card">
            {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}

function PublicLinkShare({ userId, userName, company }) {
  const [prospects, setProspects] = useState([]);
  const [selectedPid, setSelectedPid] = useState("");
  useEffect(() => {
    api.get("/prospects").then((r) => setProspects(r.data)).catch(() => {});
  }, []);

  const base = window.location.origin;
  const link = `${base}/c/${userId}${selectedPid ? `?p=${selectedPid}` : ""}`;
  const msg = `Bonjour, merci pour notre échange. Voici ma carte de visite : ${link}`;

  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Lien copié");
  };

  const selected = prospects.find((p) => p.prospect_id === selectedPid);
  const phone = selected?.phone?.replace(/\s/g, "") || "";
  const email = selected?.email || "";

  return (
    <div className="mt-4 space-y-3">
      <div>
        <div className="hp-label mb-1.5">Rattacher à un lead</div>
        <select
          value={selectedPid}
          onChange={(e) => setSelectedPid(e.target.value)}
          className="w-full rounded-lg border border-[var(--border)] p-2.5 text-sm bg-[var(--surface-alt)] text-[var(--text)] focus:outline-none focus:border-emerald-500"
          data-testid="share-prospect-select"
        >
          <option value="">— Lien générique —</option>
          {prospects.map((p) => (
            <option key={p.prospect_id} value={p.prospect_id}>{p.name} · {p.company}</option>
          ))}
        </select>
      </div>
      <div className="bg-[var(--surface-alt)] border border-[var(--border-soft)] rounded-lg p-2.5 flex items-center gap-2">
        <code className="text-xs text-zinc-300 truncate flex-1" data-testid="share-link">{link}</code>
        <Button size="sm" variant="ghost" onClick={copy} className="rounded-full h-8 px-3 shrink-0 text-zinc-400 hover:text-white hover:bg-[var(--surface)]" data-testid="copy-link">
          <Copy className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <a
          href={`sms:${phone}?body=${encodeURIComponent(msg)}`}
          className="rounded-lg p-3 bg-sky-500/15 border border-sky-500/30 hover:bg-sky-500/25 text-sky-300 text-xs font-semibold text-center transition-colors flex flex-col items-center gap-1"
          data-testid="share-sms"
        >
          <MessageCircle className="w-4 h-4" />SMS
        </a>
        <a
          href={`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg p-3 bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 text-emerald-300 text-xs font-semibold text-center transition-colors flex flex-col items-center gap-1"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="w-4 h-4" />WhatsApp
        </a>
        <a
          href={`mailto:${email}?subject=${encodeURIComponent(`Ma carte de visite — ${userName || ""}${company ? " · " + company : ""}`)}&body=${encodeURIComponent(msg)}`}
          className="rounded-lg p-3 bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 text-emerald-300 text-xs font-semibold text-center transition-colors flex flex-col items-center gap-1"
          data-testid="share-email"
        >
          <Mail className="w-4 h-4" />Email
        </a>
      </div>
      {selectedPid && !phone && !email && (
        <div className="text-xs text-amber-300">Ce lead n'a pas de téléphone/email renseignés.</div>
      )}
    </div>
  );
}
